import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import PageLayout from "@/components/custom/PageLayout";
import CarAllocationCard from "./CheckOutCarAllocationCard";
import SecondaryCurved from "@/assets/backgrounds/SecondaryCurved.svg";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AllocationService from "@/services/api/AllotmentService";
import { CarDetailsResponse } from "@/central-atoms/everest-fleet-pwa/responses";
import { useNavigate } from "react-router-dom";
import { BottomNav } from "@/components/custom/BottomNav";
import { useUserDetails } from "@/redux/hooks/useSupervisorDetails";
import { RootState } from "@/redux/store";
import SuccessErrorScreen from "@/components/custom/SuccessErrorScreen";
import ErrorMessageFullCard from "@/components/custom/ErrorMessageFullCard";

const CheckOutCarAllocation: React.FC = () => {
  const [vrn, setVrn] = useState<string>("");
  const [carDetails, setCarDetails] = useState<CarDetailsResponse | null>(null);
  const [odo, setOdo] = useState<number>(0);
  const [soc, setSoc] = useState<number>(0);
  const [allocationSuccess, setAllocationSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false); // Track API call status
  const [isAllocating, setIsAllocating] = useState<boolean>(false); // Track allocation status

  const userDetails = useUserDetails();
  const carDetailsFromRedux = useSelector(
    (state: RootState) => state.carAllocation.carDetails
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (carDetailsFromRedux) {
      setVrn(carDetailsFromRedux.vrn);
      setOdo(carDetailsFromRedux.odo);
      setSoc(carDetailsFromRedux.soc);
    }
  }, [carDetailsFromRedux]);

  const handleFetchDetailsClick = async () => {
    setIsFetching(true); // Disable button during fetch

    try {
      const reqBody = {
        vrn,
        phone: userDetails?.phone as string,
      };
      const response = await AllocationService.carDetailsFromVrn(reqBody);

      if (response.success) {
        setCarDetails(response);
        setVrn(response.data.details.vrn as string);
        setOdo(response.data.details.odo ?? 0);
        setSoc(response.data.details.soc ?? 0);
      } else {
        console.error("Failed to fetch car details", response);
        if (response?.data?.messages?.length) {
          setErrorMessage(response.data.messages);
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch car details", error);
      if (error?.response?.data?.data?.messages?.length) {
        setErrorMessage(error?.response?.data?.data?.messages);
      }
    } finally {
      setIsFetching(false); // Re-enable button after fetch
    }
  };

  const handleAllocateClick = async () => {
    setIsAllocating(true); // Disable button during allocation

    try {
      const reqBody = {
        token: carDetailsFromRedux?.token as string,
        vrn,
        phone: userDetails?.phone as string,
        edited_odometer_reading: odo,
        edited_soc: soc,
      };
      const response = await AllocationService.allotCarToPilot(reqBody);

      if (response.success) {
        setAllocationSuccess(true);
      } else {
        if (response?.data?.messages?.length) {
          setErrorMessage(response.data.messages);
        }
      }
    } catch (error: any) {
      console.error("Failed to allocate car", error);
      if (error?.response?.data?.data?.messages?.length) {
        setErrorMessage(error?.response?.data?.data?.messages);
      }
    } finally {
      setIsAllocating(false); // Re-enable button after allocation
    }
  };

  const cardDetails = {
    vrn: carDetails?.data?.details?.vrn || carDetailsFromRedux?.vrn,
    hubName: carDetails?.data?.details?.hubName || carDetailsFromRedux?.hubName,
    soc: carDetails?.data.details.soc,
    odo: carDetails?.data.details.odo,
  };

  return (
    <>
      {allocationSuccess ? (
        <SuccessErrorScreen message="Car allocation successful" type={1} />
      ) : (
        <>
          {errorMessage.length > 0 && (
            <ErrorMessageFullCard
              errorString={errorMessage}
              handleBackToHome={() => {
                setErrorMessage([]);
                navigate("/check-out/car-allocation");
              }}
            >
              <div className="pb-[10px]">
                <div className="text-secondary font-bold pb-[4px] text-[14px]">
                  Login Token: {carDetailsFromRedux?.token}
                </div>
                <div className="text-secondary font-bold pb-[4px] text-[14px]">
                  VRN: {cardDetails.vrn}
                </div>
                <div className="text-secondary font-bold pb-[4px] text-[14px]">
                  Vehicle Hub: {cardDetails.hubName}
                </div>
              </div>
            </ErrorMessageFullCard>
          )}
          <div className="relative w-full">
            <img
              src={SecondaryCurved}
              alt="Background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center mt-24">
              <CarAllocationCard
                details={cardDetails}
                odo={odo}
                setOdo={setOdo}
                soc={soc}
                setSoc={setSoc}
              />
            </div>
          </div>
          <PageLayout>
            <div className="mt-20">
              <div className="flex flex-col justify-center items-center gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  className="my-3"
                  onClick={handleAllocateClick}
                  disabled={isAllocating || vrn === ""}
                >
                  {isAllocating ? "Allocating..." : "Allocate"}
                </Button>
                {errorMessage && (
                  <p className="text-red-500 text-sm mt-2">{errorMessage}</p>
                )}
                <h1 className="text-primary">— OR —</h1>
                <h3 className="mt-12">Enter Vehicle Number</h3>
                <Input
                  variant="primary"
                  value={vrn}
                  onChange={(e) => setVrn(e.target.value)}
                />
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={handleFetchDetailsClick}
                  disabled={isFetching}
                >
                  {isFetching ? "Fetching..." : "Fetch Details"}
                </Button>
              </div>
            </div>
          </PageLayout>
          <BottomNav active={3} />
        </>
      )}
    </>
  );
};

export default CheckOutCarAllocation;
